import React, {useId, useState} from 'react'
import {cloudPathParent} from '@/shared/utils/cloudPath'
import {CloudPathLink} from '../cloudPathLinks'
import {ChatPanel} from './ChatPanel'
import type {FileChange, FileChangeOp} from '../filesChanged'

/** Entries shown before the "+N more" expander. */
const COLLAPSED_LIMIT = 8

const OP_LABELS: Record<FileChangeOp, string> = {
  created: 'Created',
  updated: 'Updated',
  moved: 'Moved from',
  copied: 'Copied from',
  deleted: 'Deleted',
  folder_created: 'Folder created',
}

const OP_ICONS: Record<FileChangeOp, string> = {
  created: 'fa-file-circle-plus',
  updated: 'fa-file-pen',
  moved: 'fa-right-left',
  copied: 'fa-copy',
  deleted: 'fa-trash-can',
  folder_created: 'fa-folder-plus',
}

function ChangeTarget({change}: {change: FileChange}): React.ReactElement {
  if (change.op === 'deleted') {
    // The path is gone, so it is plain text; its folder is the useful place to go.
    const parent = cloudPathParent(change.path)
    return (
      <>
        <del className='omics-files-changed__path omics-files-changed__path--deleted'>
          {change.path}
        </del>
        {parent !== null ? (
          <CloudPathLink
            path={parent}
            aria-label={`Open folder ${parent}`}
            className='omics-files-changed__folder-link'
          >
            <i className='fas fa-folder-open' aria-hidden /> Open folder
          </CloudPathLink>
        ) : null}
      </>
    )
  }
  return (
    <CloudPathLink path={change.path} className='omics-files-changed__path'>
      {change.path}
    </CloudPathLink>
  )
}

/** Verb of an aggregated entry: "Updated 37 files in cloud:x/". */
const AGGREGATED_VERBS: Record<FileChangeOp, string> = {
  created: 'Created',
  updated: 'Updated',
  moved: 'Moved',
  copied: 'Copied',
  deleted: 'Deleted',
  folder_created: 'Created',
}

function aggregatedLabel(change: FileChange): string {
  const count = change.count ?? 0
  const noun = change.op === 'folder_created' ? 'folder' : 'file'
  const preposition = change.op === 'moved' || change.op === 'copied' ? 'into' : 'in'
  return `${AGGREGATED_VERBS[change.op]} ${count} ${noun}${count === 1 ? '' : 's'} ${preposition}`
}

function AggregatedRow({change}: {change: FileChange}): React.ReactElement {
  return (
    <li
      className={`omics-files-changed__item omics-files-changed__item--${change.op} omics-files-changed__item--aggregated`}
    >
      <i className={`fas ${OP_ICONS[change.op]} omics-files-changed__icon`} aria-hidden />
      <span className='omics-files-changed__op'>{aggregatedLabel(change)}</span>{' '}
      <CloudPathLink path={change.path} className='omics-files-changed__path'>
        {change.path}
      </CloudPathLink>
    </li>
  )
}

function ChangeRow({change}: {change: FileChange}): React.ReactElement {
  if (change.aggregated) return <AggregatedRow change={change} />
  const hasSource = (change.op === 'moved' || change.op === 'copied') && change.from
  return (
    <li className={`omics-files-changed__item omics-files-changed__item--${change.op}`}>
      <i className={`fas ${OP_ICONS[change.op]} omics-files-changed__icon`} aria-hidden />
      {hasSource ? (
        <>
          <ChangeTarget change={change} />
          <span className='omics-files-changed__op'>{OP_LABELS[change.op]}</span>
          {change.op === 'copied' ? (
            <CloudPathLink path={change.from!} className='omics-files-changed__source'>
              {change.from}
            </CloudPathLink>
          ) : (
            // A moved file's source no longer exists.
            <span className='omics-files-changed__source'>{change.from}</span>
          )}
        </>
      ) : (
        <>
          <span className='omics-files-changed__op'>
            {change.op === 'moved'
              ? 'Moved'
              : change.op === 'copied'
              ? 'Copied'
              : OP_LABELS[change.op]}
          </span>
          <ChangeTarget change={change} />
        </>
      )}
    </li>
  )
}

/** Changes the entries stand for: an aggregated entry counts its files. */
function representedCount(changes: readonly FileChange[]): number {
  return changes.reduce((sum, c) => sum + (c.aggregated ? c.count ?? 0 : 1), 0)
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

/** Note under the title when the list is shorter than what the turn changed, else null. */
function truncationNote(
  changes: readonly FileChange[],
  total: number | undefined,
  truncated: boolean
): string | null {
  if (!truncated) return null
  if (!changes.length) {
    return total !== undefined
      ? `The list of ${plural(total, 'change')} is too large to show`
      : 'The list of changes is too large to show'
  }
  const shown = representedCount(changes)
  if (total === undefined) return `Showing ${plural(shown, 'change')}; the list is incomplete`
  if (shown < total) return `Showing ${shown} of ${plural(total, 'change')}`
  return changes.some((c) => c.aggregated) ? 'Changes are grouped by folder' : null
}

/**
 * "Files changed" panel of the run footer: what the turn wrote to the user's cloud storage. A
 * shortened list (`truncated`, with the full `total` when known) says how much of it is shown.
 * Collapsible like Working (ChatPanel); closed by default.
 */
export function FilesChangedSection({
  changes: changesProp,
  total,
  truncated = false,
}: {
  changes: readonly FileChange[] | undefined
  total?: number
  truncated?: boolean
}): React.ReactElement | null {
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const listId = useId()
  const changes = changesProp ?? []
  const knownTotal = truncated ? total : undefined
  if (!changes.length && !(truncated && (knownTotal ?? 0) > 0)) return null

  const hidden = changes.length - COLLAPSED_LIMIT
  const visible = expanded || hidden <= 0 ? changes : changes.slice(0, COLLAPSED_LIMIT)
  const note = truncationNote(changes, knownTotal, truncated)
  const count = knownTotal ?? representedCount(changes)

  return (
    <ChatPanel
      icon='fa-file-pen'
      title={`Files changed (${count})`}
      open={open}
      onToggle={() => setOpen((v) => !v)}
      ariaLabel='Files changed'
      className='omics-files-changed'
      testId='omics-files-changed'
    >
      {note ? <p className='omics-files-changed__note'>{note}</p> : null}
      {changes.length ? (
        <ul id={listId} className='omics-chat-panel__list'>
          {visible.map((change, i) => (
            <ChangeRow key={`${change.op}:${change.path}:${i}`} change={change} />
          ))}
        </ul>
      ) : null}
      {hidden > 0 ? (
        <button
          type='button'
          className='omics-files-changed__more'
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={listId}
        >
          {expanded ? 'Show less' : `+${hidden} more`}
        </button>
      ) : null}
    </ChatPanel>
  )
}
